import { Link, useParams } from 'react-router'
import Loader from '../../common/Loader'
import { AssessmentsTable } from '../Assessments'
import { useCallback, useState } from 'react'
import { getMe } from '../../MeProvider.ts'
import { useOrgAuth } from '../../OrgAuthProvider.ts'
import {
	useCreateAssessment,
	useGetAssessments,
	useGetFullTrajectory,
	useGetOrganizationUsers,
	useGetUserOrganizationRolesV2,
	useGetUserPositionAssessments,
	useGetUserV2,
	useDeleteEmployee,
	useGetPaginatedAssessments,
} from '../../http/hooks.ts'
import { toast } from 'react-toastify'
import { EmployeesTable } from './index.tsx'
import { useModal } from '../../components/modals/useModal.ts'
import { useQueryClient } from '@tanstack/react-query'
import { IPositionTrajectory, IUser } from '../../services/nextpeakClient'
import { LatestAssessment, PositionAssessments } from '../Home'
import { Page, PageProps } from '../../layout/Page.tsx'
import TrajectoryCounter from '../../newComponents/TrajectoryCounter/index.tsx'
import Card from '../../newComponents/Card/Card.tsx'
import { Button } from '../../newComponents/Button/Button.tsx'
import { TabData, Tabs } from '../../newComponents/Tabs/index.tsx'
import { useNextPeakClient } from '../../services/nextpeakClient/clientProvider.tsx'
import { useNavigate } from 'react-router'

const toSnakeCase = (str: string): string => {
	const words = str.split(' ')

	return words.map((word) => word.toLowerCase()).join('_')
}

export const CareerPath = ({ employee, trajectoryItems }: { employee: IUser; trajectoryItems: IPositionTrajectory[] }) => {
	const trajectoryName = trajectoryItems[0].path

	return (
		<>
			<h3 className="text-2xl mb-6 mt-4 font-semibold text-black text-center">{trajectoryName}</h3>
			<Card>
				<TrajectoryCounter employee={employee} trajectoryItems={trajectoryItems} />
			</Card>
		</>
	)
}

const CareerPathView = ({ employee }: { employee: IUser }) => {
	const client = useNextPeakClient()
	const { data, isLoading } = useGetFullTrajectory(client, {
		path: toSnakeCase(employee.userPosition!.positionTrajectory!.fromPosition!.role) + '_path',
	})
	const { data: assessmentData, isLoading: isLoadingAssessments } = useGetUserPositionAssessments(client, {
		userId: employee.id!,
		filters: {
			latest: 0,
			isCompleted: 1,
			includes: ['assessmentResponses', 'assessmentResponses.answers'],
		},
	})

	return (
		<div>
			{isLoading && <Loader />}
			{!isLoading && data && <CareerPath employee={employee} trajectoryItems={data} />}
			{isLoadingAssessments && <Loader />}
			<div className="mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6 2xl:mt-7.5 2xl:gap-7.5">
				<PositionAssessments data={assessmentData} isLoading={isLoadingAssessments} />
				<LatestAssessment data={assessmentData} isLoading={isLoadingAssessments} />
			</div>
		</div>
	)
}

const SubordinatesTableView = ({ employeeId }: { employeeId: string }) => {
	const {
		authOrgUser: { orgId },
	} = useOrgAuth()
	const me = getMe()
	const { onOpen } = useModal('InviteUserModal')
	const client = useNextPeakClient()
	const { data: employees } = useGetOrganizationUsers(client, { organizationId: orgId, managerId: employeeId }, { enabled: !!orgId })

	// Fetch all assessments for subordinates with pagination
	const { data: assessmentsData, refetch: refetchAssessments } = useGetAssessments(
		client,
		{
			managerUserId: employeeId,
			includes: ['assessmentResponses'],
		},
		{
			enabled: !!orgId && !!employees,
		}
	)

	const mutation = useCreateAssessment(client)

	const handleCreateAssessment = useCallback(
		async (subordinateId: string) => {
			try {
				const data = {
					employeeUserId: subordinateId,
					managerUserId: employeeId,
					organizationId: orgId,
				}

				await mutation.mutateAsync(data)
				toast.success('Assessment created')
				refetchAssessments()
			} catch (e) {
				console.error(e)
				toast.error('Failed to create assessment')
			}
		},
		[mutation, employeeId, orgId, refetchAssessments]
	)

	const iamManager = me.organizationRoles.includes('manager')
	const iamAdmin = me.organizationRoles.includes('admin')

	const iCanInvite = iamManager || iamAdmin

	return (
		<>
			{iCanInvite && (
				<div className="flex justify-end mb-3">
					<Button onClick={() => onOpen({ managerId: employeeId })} color="secondary">
						Invite subordinate
					</Button>
				</div>
			)}
			{employees && assessmentsData && (
				<EmployeesTable employees={employees} pendingAssessments={assessmentsData} onCreateAssessment={handleCreateAssessment} />
			)}
		</>
	)
}

const AssessmentsTableView = ({ employeeId, employeeManagerId }: { employeeId: string; employeeManagerId?: string }) => {
	const queryClient = useQueryClient()
	const {
		authOrgUser: { orgId },
	} = useOrgAuth()
	const me = getMe()

	const isAuthUserEmployeeManager = me.id === employeeManagerId
	const isAuthUserEmployee = me.id === employeeId

	const client = useNextPeakClient()
	const [currentPage, setCurrentPage] = useState(1)
	const ITEMS_PER_PAGE = 5

	const {
		data: assessmentsData,
		isLoading,
		isSuccess,
	} = useGetPaginatedAssessments(
		client,
		{
			employeeUserId: employeeId,
			includes: ['assessmentResponses', 'assessmentType', 'assessmentType.position', 'employee'],
			limit: ITEMS_PER_PAGE,
			offset: (currentPage - 1) * ITEMS_PER_PAGE,
		},
		{ enabled: isAuthUserEmployeeManager || isAuthUserEmployee }
	)

	const mutation = useCreateAssessment(client)

	const handleSubmit = useCallback(async () => {
		try {
			if (!employeeManagerId) {
				toast.error('Employee does not have a manager')
				return
			}
			if (!isAuthUserEmployeeManager) {
				toast.error('You are not allowed to create assessments for this user')
				return
			}
			const data = { employeeUserId: employeeId, managerUserId: employeeManagerId, organizationId: orgId }
			await mutation.mutateAsync(data)

			toast.success('Assessment created')
			// @ts-ignore
			queryClient.invalidateQueries(['UserAssessments', { userId: employeeId, userIsManager: false }])
		} catch (e) {
			console.error(e)
			toast.error('Failed to create assessment')
		}
	}, [mutation])

	if (isLoading && !isSuccess) {
		return <Loader />
	}

	return (
		<>
			{isAuthUserEmployeeManager && (
				<div className="flex justify-end mb-3">
					<Button onClick={handleSubmit} color="secondary">
						Create assessment
					</Button>
				</div>
			)}
			{(isAuthUserEmployeeManager || isAuthUserEmployee) && assessmentsData && (
				<AssessmentsTable
					assessments={assessmentsData.data}
					currentPage={currentPage}
					totalItems={assessmentsData.total}
					onPageChange={setCurrentPage}
					loading={isLoading}
				/>
			)}
			{!(isAuthUserEmployeeManager || isAuthUserEmployee) && <div>You are not allowed to see anything here</div>}
		</>
	)
}

const TabbedTableView = ({
	employeeId,
	employeeManagerId,
	employee,
	roles,
}: {
	employeeId: string
	employeeManagerId?: string
	roles: { name: string; id: string }[]
	employee: any
}) => {
	const [openTab, setOpenTab] = useState(1)

	const isUserManager = roles.some((role) => role.name === 'manager')

	const tabs: TabData[] = [
		{ id: 1, label: 'Assessments' },
		{ id: 3, label: 'Career path' },
	]

	if (isUserManager) tabs.push({ id: 2, label: 'Subordinates' })

	return (
		<div>
			<Tabs
				tabs={tabs.sort((a, b) => Number(a.id) - Number(b.id))}
				activeTabId={openTab}
				onTabChange={(tabId) => setOpenTab(Number(tabId))}
			/>
			<div>
				<div className={`${openTab === 1 ? 'block' : 'hidden'}`}>
					<AssessmentsTableView employeeId={employeeId} employeeManagerId={employeeManagerId} />
				</div>
				{isUserManager && (
					<div className={`${openTab === 2 ? 'block' : 'hidden'}`}>
						<SubordinatesTableView employeeId={employeeId} />
					</div>
				)}
				<div className={`${openTab === 3 ? 'block' : 'hidden'}`}>
					<CareerPathView employee={employee} />
				</div>
			</div>
		</div>
	)
}

const EmployeeDetail = () => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { onOpen: openEditRoles } = useModal('EditRolesModal')
	const { onOpen: openPromote } = useModal('PromoteEmployeeModal')
	const { onOpen: openChangeManager } = useModal('ChangeManagerModal')
	const deleteModal = useModal('DeleteConfirmationModal')
	const { employeeId } = useParams()
	const me = getMe()
	const {
		authOrgUser: { orgId },
	} = useOrgAuth()

	const pageProps: PageProps = {
		title: 'Employee',
		breadcrumbs: 'Employee',
	}

	const isAuthUserAdmin = me.organizationRoles.includes('admin')

	if (!employeeId || employeeId === 'undefined') {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<div>How is this even possible</div>
				</div>
			</Page>
		)
	}

	const client = useNextPeakClient()
	const { data: employee, isLoading } = useGetUserV2(client, {
		userId: employeeId,
		includes: [
			'organization',
			'userPosition',
			'userPosition.positionTrajectory',
			'userPosition.positionTrajectory.fromPosition',
			'managerRelation',
			'managerRelation.manager',
		],
	})
	const { data: roles, isLoading: isLoadingRoles } = useGetUserOrganizationRolesV2(client, employeeId)
	const deleteMutation = useDeleteEmployee(client)

	const isUserLoading = isLoading || isLoadingRoles
	const isAuthUserEmployeeManager = me.id === employee?.managerRelation?.managerId

	const handleDeleteEmployee = useCallback(async () => {
		if (!employee) return

		try {
			await deleteMutation.mutateAsync({
				userId: employeeId,
				organizationId: orgId,
			})

			// Invalidate relevant queries
			queryClient.invalidateQueries({ queryKey: ['GetOrganizationUsers'] })
			queryClient.invalidateQueries({ queryKey: ['GetOrganizationUsersV2'] })

			// Navigate back to employees list
			navigate('/employees')
		} catch (error) {
			console.error('Failed to delete employee:', error)
		}
	}, [employee, employeeId, orgId, deleteMutation, queryClient, navigate])

	const handleOpenDeleteModal = useCallback(() => {
		deleteModal.onOpen({
			employeeName: employee?.name,
			employeeId: employeeId,
			organizationId: orgId,
			onConfirm: handleDeleteEmployee,
		})
	}, [deleteModal, employee?.name, employeeId, orgId, handleDeleteEmployee])

	return (
		<Page {...pageProps}>
			{isUserLoading && <Loader />}
			{!isUserLoading && (
				<>
					<div className="grid grid-cols-12 gap-8">
						<Card className="col-span-12">
							{employee && (
								<div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
									<div className="flex flex-col items-center w-full gap-6 xl:flex-row">
										<div className="w-20 h-20 overflow-hidden border border-gray-200 rounded-full dark:border-gray-800">
											{employee.avatar && <img src={employee.avatar} alt="profile_picture" />}
											{!employee.avatar && (
												<img
													src="https://static.vecteezy.com/system/resources/thumbnails/003/337/584/small/default-avatar-photo-placeholder-profile-icon-vector.jpg"
													alt="profile_picture"
												/>
											)}
										</div>
										<div className="order-3 xl:order-2">
											<h4 className="mb-2 text-lg font-semibold text-center text-gray-800 dark:text-white/90 xl:text-left">
												{employee.name}
											</h4>
											<div className="flex flex-col items-center gap-1 text-center xl:flex-row xl:gap-3 xl:text-left">
												<p className="text-sm text-gray-500 dark:text-gray-400">
													{employee.userPosition?.positionTrajectory?.fromPosition?.title}
												</p>
												{employee.managerRelation?.manager?.name && (
													<>
														<div className="hidden h-3.5 w-px bg-gray-300 dark:bg-gray-700 xl:block"></div>
														<span className="text-sm text-gray-500 dark:text-gray-400">
															Managed By{' '}
															<Link
																className="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
																to={`/employees/${employee.managerRelation?.manager?.id}`}
															>
																{employee.managerRelation?.manager?.name}
															</Link>
														</span>
													</>
												)}
												<div className="hidden h-3.5 w-px bg-gray-300 dark:bg-gray-700 xl:block"></div>
												<p className="text-sm text-gray-500 dark:text-gray-400">{employee.organization?.name}</p>
											</div>
										</div>
										<div className="flex items-center order-2 gap-2 grow xl:order-3 xl:justify-end">
											{isAuthUserAdmin && (
												<Button onClick={() => openEditRoles({ employeeId })} color="secondary" size="sm">
													Edit user roles
												</Button>
											)}
											{(isAuthUserEmployeeManager || isAuthUserAdmin) && (
												<Button
													onClick={() => openChangeManager({ employeeId, organizationId: employee.organization!.id })}
													color="secondary"
													size="sm"
												>
													Change manager
												</Button>
											)}
											{isAuthUserEmployeeManager && (
												<Button onClick={() => openPromote({ employeeId })} color="secondary" size="sm">
													Promote
												</Button>
											)}
											{isAuthUserEmployeeManager && (
												<Button onClick={handleOpenDeleteModal} color="danger" size="sm">
													Delete
												</Button>
											)}
										</div>
									</div>
								</div>
							)}
						</Card>
					</div>
					<div className="flex flex-col gap-10 mt-4">
						{employee && roles && (
							<TabbedTableView
								employeeId={employeeId}
								employeeManagerId={employee.managerRelation?.managerId}
								roles={roles}
								employee={employee}
							/>
						)}
					</div>

					{/* Delete Confirmation Modal */}
				</>
			)}
		</Page>
	)
}

export default EmployeeDetail
